import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Link from 'gatsby-link';
import Img from 'gatsby-image';
import { css, cx, keyframes } from 'react-emotion';
import toTitleCase from 'to-title-case';
import Color from 'color';

import gray from '../utils/gray';
import presets, { colors, baselineGrid, zIndex } from '../utils/presets';
import { options, adjustFontSizeTo } from '../utils/typography';
import { toRem, toVW, mqObj as mq } from '../utils/functions';

import Arrow from '../assets/svg-sprite/shape-button-arrow.svg';

const { curve, speed } = presets.animation;

const browserChromeDotColor = colors.gray.calm;

const browserChromeDotColors = [
  Color(browserChromeDotColor)
    .hsl()
    .string(),
  Color(browserChromeDotColor)
    .hsl()
    .alpha(0.76)
    .string(),
  Color(browserChromeDotColor)
    .hsl()
    .alpha(0.52)
    .string(),
];

const cover = {
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
};

const themePreview = css`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100vw;
  margin: 0 0 ${toRem(baselineGrid * 4)};
  background-color: ${colors.ui.whisper};
  border-radius: ${presets.radius.default};
  overflow: hidden;

  /* pseudo-background on hover */
  &::before {
    content: '';
    display: block;
    ${cover};
    z-index: ${zIndex[1]};
    opacity: 0;
  }

  &:hover,
  &:active,
  &:focus {
    .themePreviewScreenshot {
      body:not(.touch-enabled) & {
        transform: translate3d(0, 0, 0);
      }
    }
  }

  ${mq.palm} {
    margin: 0 0 ${toRem(baselineGrid * 4)};
  }

  @media (min-width: 480px) {
    margin: 0 0 ${toRem(baselineGrid * 4)};
  }

  ${mq.lap} {
    width: calc(50% - ${toRem(baselineGrid * 2)});
  }

  ${mq.lapLarge} {
    width: calc(50% - ${toVW(baselineGrid * 4)});
  }

  ${mq.desk} {
    width: calc(33.3333% - ${toVW(baselineGrid * 5)});
  }

  @media (min-width: 1568px) {
    margin: 0 0 ${toRem(baselineGrid * 6)};
  }
`;

const themePreviewInfo = css`
  position: relative;
  z-index: ${zIndex[2]};
  padding: ${toRem(baselineGrid * 4)} ${toRem(baselineGrid * 3)} ${toRem(baselineGrid * 3)};

  ${mq.palm} {
    padding: ${toRem(baselineGrid * 4)} ${toRem(baselineGrid * 3)} ${toRem(baselineGrid * 3)};
  }

  @media (min-width: 480px) {
    padding: ${toRem(baselineGrid * 4)} ${toRem(baselineGrid * 4)} ${toRem(baselineGrid * 3)};
  }

  ${mq.lapLarge} {
    padding: ${toRem(baselineGrid * 4)};
  }
`;

const themePreviewLink = css({
  ...cover,
  zIndex: zIndex[3],
});

const themePreviewName = css`
  padding: 0;
  margin: 0 0 ${toRem(baselineGrid * 0.5)} 0;
  font-family: ${options.headerFontFamily.join(',')};
  font-size: ${toRem(24)};
  letter-spacing: ${1 / 21}em;
`;

const themePreviewPlug = css`
  font-family: ${options.headerFontFamily.join(',')};
  font-weight: ${options.headerWeight};
  ${adjustFontSizeTo('16px')};
  line-height: 1.3333;
  margin: 0;
  color: ${colors.gray.gunpowder};
`;

const themePreviewScreenshot = css`
  position: relative;
  margin-left: ${toRem(baselineGrid * 3)};
  padding-top: ${toRem(baselineGrid * 2)};
  z-index: ${zIndex[2]};
  box-shadow: 0 2px 5px ${gray(80)};

  body:not(.touch-enabled) & {
    transform: translate3d(0, ${baselineGrid * 2}px, 0);
    transition: transform ${speed.medium} ${curve.easeInOutCirc};
  }

  ${mq.palm} {
    margin-left: ${toRem(baselineGrid * 3)};
  }

  @media (min-width: 480px) {
    margin-left: ${toRem(baselineGrid * 4)};
  }

  /* browser chrome */
  &::before {
    content: '';
    display: block;
    position: absolute;
    height: ${toRem(baselineGrid * 2)};
    top: 0;
    right: 0;
    left: 0;
    background-color: ${gray(70, 'cool')};
    border-top-left-radius: 5px;
  }

  &::after {
    content: '';
    display: block;
    position: absolute;
    width: 6px;
    height: 6px;
    top: ${toRem(baselineGrid)};
    left: ${toRem(baselineGrid)};
    background-color: ${browserChromeDotColors[0]};
    transform: translate(0, -50%);
    border-radius: 50%;
    box-shadow: 10px 0 0 0 ${browserChromeDotColors[1]}, 20px 0 0 0 ${browserChromeDotColors[2]};
  }
`;

const screenshotWrapper = css`
  position: relative;
  padding-bottom: 50vh;
  overflow: hidden;

  ${mq.lap} {
    padding-bottom: ${(4 / 4 * 100).toFixed(presets.precision)}%;
  }

  ${mq.deskLarge} {
    padding-bottom: ${(5 / 6 * 100).toFixed(presets.precision)}%;
  }
`;

const screenshotFrame = css`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
`;

const screenshot = css``;

const ghostPreviewColor = Color(colors.ui.whisper)
  .hsl()
  .darken(0.1)
  .string();

const ghostPreview = css`
  background-color: ${colors.ui.bright};

  &::after {
    content: '';
    display: block;
    ${cover};
    z-index: ${zIndex[0]};
    box-shadow: inset 0 0 0 ${toRem(baselineGrid)} ${colors.ui.whisper};
  }

  .themePreviewName {
    color: ${ghostPreviewColor};
    user-select: none;
  }
`;

const GhostPreview = ({ className }) => (
  <div className={cx(themePreview, ghostPreview, className)}>
    <div css={themePreviewInfo}>
      <h1 className="themePreviewName" css={themePreviewName}>
        {'Coming Soon'}
      </h1>
    </div>
  </div>
);

GhostPreview.propTypes = {
  className: PropTypes.string,
};

const teaserColor = gray(64, 'cool');
const teaserBgColor = colors.ui.bright;
const teaserBgColorHover = colors.pixelmoxie;
const teaserGradStop = Color(teaserBgColorHover)
  .hsl()
  .rotate(8)
  .string();
const teaserColorHover = Color(teaserBgColorHover).isLight()
  ? colors.gray.gunpowder
  : colors.ui.bright;

const teaserPreview = css`
  color: ${teaserColor};
  background-color: ${teaserBgColor};
  transition: color ${speed.default} ${curve.default};

  &::before {
    background-color: ${teaserBgColorHover};
    background-image: linear-gradient(to top right, ${teaserBgColorHover} 60%, ${teaserGradStop});
    transform: translate(0, ${toRem(baselineGrid * 4)}) scale(0.96);
    transition-property: opacity, transform;
    transition-duration: ${speed.default};
    transition-timing-function: ${curve.default};
  }

  &::after {
    content: '';
    display: block;
    ${cover};
    z-index: ${zIndex[0]};
    box-shadow: inset 0 0 0 ${toRem(baselineGrid)} ${teaserColor};
    transition: transform ${speed.default} ${curve.default};
  }

  &:hover,
  &:active,
  &:focus {
    color: ${teaserColorHover};

    &::before {
      opacity: 1;
      transform: translate(0, 0) scale(1);
    }

    &::after {
      transform: scale(1.05);
    }
  }

  .themePreviewName {
    color: currentColor;
    user-select: none;
  }

  .teaser,
  .arrow {
    display: inline;
    vertical-align: middle;
  }

  .arrow {
    margin: 0 ${toRem(baselineGrid * 0.5)};
    width: 1em;
    height: 1em;
    transform: scale(1.25) translateY(-1px);
    transform-origin: center;
    fill: currentColor;
  }
`;

const TeaserPreview = ({ className, teaser, linkTo }) => (
  <div className={cx(themePreview, teaserPreview, className)}>
    <Link to={linkTo} css={themePreviewLink} />
    <div css={themePreviewInfo}>
      <h1 className="themePreviewName" css={themePreviewName}>
        <span className={'teaser'}>{toTitleCase(teaser)}</span>
        <svg className={'arrow'} viewBox={Arrow.viewBox}>
          <use xlinkHref={`#${Arrow.id}`} />
        </svg>
      </h1>
    </div>
  </div>
);

TeaserPreview.propTypes = {
  className: PropTypes.string,
  teaser: PropTypes.string.isRequired,
  linkTo: PropTypes.string.isRequired,
};

class ThemePreview extends Component {
  constructor(props) {
    super(props);

    this.state = {
      hover: false,
    };

    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
  }

  handleMouseEnter() {
    this.setState({ hover: true });
  }

  handleMouseLeave() {
    this.setState({ hover: false });
  }

  render() {
    const { theme, coloured } = this.props;
    const { slug } = theme.fields;
    const { name, plug, previewImage, previewBagRotate } = theme.frontmatter;

    const bagColorRotation = parseInt(previewBagRotate, 10) || -8;

    const backgroundColor = theme.fields.previewColors.background
      ? Color(theme.fields.previewColors.background).hsl()
      : null;

    const gradientStop = backgroundColor.rotate(bagColorRotation);

    const titleColor = theme.fields.previewColors.title
      ? Color(theme.fields.previewColors.title).hsl()
      : null;

    const mouseEnter = keyframes`
      from { opacity: 0; transform: translate(0, ${toRem(baselineGrid * 1)}); }
      to { opacity: 1;}
    `;

    const mouseLeave = keyframes`
      from { opacity: 0; transform: translate(0, ${toRem(baselineGrid * 1)}); }
      to { opacity: 1;}
    `;

    const colouredBg = css`
      &::before {
        background-color: ${backgroundColor.string()};
        background-image: linear-gradient(
          to top right,
          ${backgroundColor.string()} 60%,
          ${gradientStop.string()}
        );
        transform: translate(0, ${toRem(baselineGrid * 4)}) scale(0.96);
        transition-property: opacity, transform;
        transition-duration: ${speed.default};
        transition-timing-function: ${curve.default};
      }

      &::after {
        content: '';
        display: block;
        ${cover};
        z-index: ${zIndex[0]};
        box-shadow: inset 0 0 0 ${toRem(baselineGrid)} ${backgroundColor.string()};
        transition: transform ${speed.default} ${curve.default};
      }

      &:hover,
      &:active,
      &:focus {
        &::before {
          transform: translate(0, 0) scale(1);
        }

        &::after {
          transform: scale(1.05);
        }
      }

      .themePreviewName,
      .themePreviewPlug {
        animation: ${mouseEnter} ${speed.medium} ${curve.default};
      }
    `;

    const colouredPreview =
      backgroundColor && titleColor
        ? css`
            background-color: ${colors.gray.gunpowder};

            &::before {
              opacity: 1;
            }

            .themePreviewName {
              color: ${titleColor.string()};
            }

            .themePreviewPlug {
              color: ${backgroundColor.isLight() ? colors.ui.copy : '#fff'};
            }

            &:hover,
            &:active,
            &:focus {
              &::before {
                opacity: 0;
              }

              .themePreviewName,
              .themePreviewPlug {
                color: ${colors.ui.bright};
              }
            }

            &.mouse-enter {
              .themePreviewName,
              .themePreviewPlug {
                animation: ${mouseEnter} ${speed.default} ${curve.default};
              }
            }

            &.mouse-leave {
              .themePreviewName,
              .themePreviewPlug {
                animation: ${mouseLeave} ${speed.default} ${curve.default};
              }
            }
          `
        : '';

    const colouredOnHover =
      backgroundColor && titleColor
        ? css`
            &::before {
              opacity: 0;
            }

            .themePreviewName,
            .themePreviewPlug {
              transition: color ${speed.default} ${curve.default};
            }

            &:hover,
            &:active,
            &:focus {
              &::before {
                opacity: 1;
              }

              .themePreviewName {
                color: ${titleColor.string()};
              }

              .themePreviewPlug {
                color: ${backgroundColor.isLight() ? colors.ui.copy : '#fff'};
              }
            }
          `
        : '';

    return (
      <article
        onMouseEnter={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
        className={cx([
          themePreview,
          colouredBg,
          () => (coloured ? colouredPreview : colouredOnHover),
          () => (this.state.hover ? 'mouse-enter' : 'mouse-leave'),
        ])}
      >
        <Link to={`/theme${slug}`} css={themePreviewLink} />
        <div css={themePreviewInfo}>
          <h1 className="themePreviewName" css={themePreviewName}>
            {name}
          </h1>
          <p className="themePreviewPlug" css={themePreviewPlug}>
            {plug}
          </p>
        </div>
        <div className="themePreviewScreenshot" css={themePreviewScreenshot}>
          <div css={screenshotWrapper}>
            <div css={screenshotFrame}>
              <div css={screenshot}>
                <Img alt={`${name} - ${plug}`} sizes={previewImage.childImageSharp.sizes} />
              </div>
            </div>
          </div>
        </div>
      </article>
    );
  }
}

ThemePreview.propTypes = {
  theme: PropTypes.object.isRequired,
  coloured: PropTypes.bool,
};

export default ThemePreview;

export { GhostPreview, TeaserPreview, themePreview };

export const themePreviewFragment = graphql`
  fragment themePreview_details on MarkdownRemark {
    id
    fields {
      order
      slug
      previewColors {
        background
        title
      }
    }
    frontmatter {
      platform
      name
      plug
      previewBagRotate
      previewImage {
        childImageSharp {
          sizes(maxWidth: 700) {
            ...GatsbyImageSharpSizes
          }
        }
      }
    }
  }
`;



// WEBPACK FOOTER //
// ./src/components/theme-preview.js